// Section 24.14 actions: the only writers of the store. Each checks the 24.7 status machine and
// throws SimError('bad_status') when called in the wrong status; withBoundary turns sim errors
// into an error card and restores the last autosave.
import {
  emptyDecision,
  newGame as simNewGame,
  resumeStep,
  step,
  validateDecision,
  type GameState,
  type NewGameOptions,
  type PromptAnswer,
  type PromptId,
} from '../../sim/api';
import { latestAutosave, loadEnvelope } from '../../persist';
import { withBoundary } from '../boundary';
import { requireGame, requireStatus } from '../guards';
import { cfgOption, newRunId, runConfig, setRun } from '../run';
import { autosave, enterDeciding, enterLoaded, refreshSaves } from '../session';
import { draft, game, initialUi, patchUi, status, ui } from '../state';
import { currentStorage } from '../storage';
import { stopObserver } from './observer-timer';

// Other streams' action files import these guards from here (M1 layout).
export { requireGame, requireStatus } from '../guards';

/** After a season resolves: pause on a prompt, end, or open the next Report (phase 1). */
function afterSeason(state: GameState): void {
  game.value = state;
  if (state.phaseCursor !== null) {
    status.value = 'paused';
    patchUi({ overlay: 'shortage' });
    return;
  }
  draft.value = emptyDecision(state);
  const ended = state.outcome !== null && !state.sandbox;
  // Section 20: the win and loss screen is the Ledger (its Back leads to the final report).
  patchUi({
    overlay: ended ? 'ledger' : 'report',
    autoFill: null,
    confirmingEnd: false,
    promptId: null,
  });
  if (ended) {
    status.value = 'ended';
    stopObserver();
    return;
  }
  // Section 3 observer mode: the player watches the steward play. A report overlay after every
  // season would cover the Play, Pause and Step controls (M6-11), so the season opens straight
  // at phase 2; the reports are in the History tab's archive.
  if (state.options.observer) {
    patchUi({ overlay: null });
    enterDeciding(state);
    return;
  }
  status.value = 'reporting';
  // The next season's phase 2 starts from this state (Section 4 autosave).
  autosave(state);
}

export const newGame = withBoundary((options: NewGameOptions, seed: string): void => {
  requireStatus('title', 'ended', 'deciding', 'reporting');
  stopObserver();
  const state = simNewGame({ ...options, seed }, seed);
  setRun({ runId: newRunId(seed), cfg: undefined, envelope: null });
  patchUi({ ...initialUi, hasContinue: ui.value.hasContinue });
  game.value = state;
  if (!state.options.ironman) autosave(state, 'start');
  enterDeciding(state);
});

/** Title-screen "Continue": resume from the newest autosave. */
export const continueGame = withBoundary((): boolean => {
  requireStatus('title');
  const storage = currentStorage();
  const found = storage === null ? null : latestAutosave(storage);
  if (found === null) return false;
  patchUi({ ...initialUi, hasContinue: true });
  enterLoaded(loadEnvelope(found.envelope), found.meta.runId);
  return true;
});

export const hasAutosave = (): boolean => {
  refreshSaves();
  return ui.value.hasContinue;
};

export const dismissReport = withBoundary((): void => {
  requireStatus('reporting', 'ended');
  patchUi({ overlay: null });
  if (status.value === 'reporting') enterDeciding(requireGame());
});

export const endSeason = withBoundary((): void => {
  requireStatus('deciding');
  const state = requireGame();
  const decision = draft.value ?? emptyDecision(state);
  const problems = validateDecision(state, decision, runConfig());
  patchUi({ confirmingEnd: false });
  if (problems.length > 0) {
    patchUi({ problems: problems.map((p) => p.message) });
    return;
  }
  patchUi({ problems: [], selectedPersonId: null, hoveredPersonId: null });
  status.value = 'resolving';
  try {
    afterSeason(step(state, decision, cfgOption()).state);
  } catch (error) {
    status.value = 'deciding';
    throw error;
  }
});

/** Answer a pausing prompt (the shortage dialog) and resume the season (ADR-0007). */
export const answerPrompt = withBoundary((promptId: PromptId, answer: PromptAnswer): void => {
  requireStatus('paused');
  status.value = 'resolving';
  try {
    afterSeason(resumeStep(requireGame(), promptId, answer, cfgOption()).state);
  } catch (error) {
    status.value = 'paused';
    throw error;
  }
});

export const toTitle = withBoundary((): void => {
  stopObserver();
  game.value = null;
  draft.value = null;
  status.value = 'title';
  setRun({ runId: '', cfg: undefined, envelope: null });
  patchUi({ ...initialUi });
  refreshSaves();
});

export const dismissError = (): void => {
  patchUi({ error: null });
};
